import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import { useParams, useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaDownload, FaFilePdf, FaSpinner } from 'react-icons/fa';
import { MdFlag } from 'react-icons/md';
import '../../HR Manager/screens/EmployeeGoals.css';
import API_BASE_URL from '../../../config/api';

// Add a styled modal component
const Modal = ({ isOpen, onClose, children, style }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(30, 41, 59, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        padding: '32px 28px 24px 28px',
        borderRadius: 16,
        minWidth: 340,
        maxWidth: 420,
        boxShadow: '0 8px 32px rgba(30,41,59,0.18)',
        position: 'relative',
        fontFamily: 'inherit',
        animation: 'modalFadeIn 0.2s',
        ...style,
      }}>
        {/* Close icon */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            background: 'none',
            border: 'none',
            fontSize: 22,
            color: '#64748b',
            cursor: 'pointer',
            fontWeight: 600,
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          &times;
        </button>
        {children}
      </div>
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const EmployeeGoalsReview = () => {
  const baseUrl = API_BASE_URL
  const [goals, setGoals] = useState([]);
  const { id } = useParams()
  const location = useLocation()
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const employeeId = location.state?.employeeId || id; // fallback to URL param
  const [employeeName, setEmployeeName] = useState('');
  const navigate = useNavigate();

  // Year filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filteredGoals, setFilteredGoals] = useState([]);

  // Function to filter goals by year
  const filterGoalsByYear = (goalsList, year) => {
    console.log(`Filtering ${goalsList.length} goals for year: ${year}`);
    const filtered = goalsList.filter(goal => {
      // Use goal.year directly if it exists
      if (goal.year !== undefined && goal.year !== null) {
        console.log(`Goal ${goal.id}: year=${goal.year}, matches=${goal.year === year}`);
        return goal.year === year;
      }

      // Fallback to checking various date fields if goal.year doesn't exist
      const goalDate = goal.created_at || goal.updated_at || goal.start_date || goal.end_date || goal.date_created;
      if (!goalDate) {
        console.log(`Goal ${goal.id}: no year or date field, including`);
        return true; // If no date, include the goal
      }

      const goalYear = new Date(goalDate).getFullYear();
      console.log(`Goal ${goal.id}: date=${goalDate}, extracted year=${goalYear}, matches=${goalYear === year}`);
      return goalYear === year;
    });

    console.log(`Filtered result: ${filtered.length} goals for year ${year}`);
    return filtered;
  };

  // Add global CSS animation for blinking unreviewed goals and download button
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blinkUnreviewed {
        0% { background-color: #fef3c7 !important; }
        50% { background-color: #fbbf24 !important; }
        100% { background-color: #fef3c7 !important; }
      }
      .unreviewed-row {
        animation: blinkUnreviewed 1.5s infinite !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [hodScore, setHodScore] = useState('');
  const [agreedScore, setAgreedScore] = useState('');
  const [modalError, setModalError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [agreedModalOpen, setAgreedModalOpen] = useState(false);
  const [viewGoal, setViewGoal] = useState(null);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // Only include goals with status 'complete'
        const completedGoals = (Array.isArray(response?.data?.employee?.goals) ? response?.data?.employee?.goals : []).filter(goal => goal.status === 'complete' || goal.status === 'completed');
        setGoals(completedGoals);
        setEmployeeName(response?.data?.employee?.name || response?.data?.employee?.full_name || '');

        // Apply year filter to the fetched goals
        const yearFilteredGoals = filterGoalsByYear(completedGoals, selectedYear);
        setFilteredGoals(yearFilteredGoals);
      } catch (err) {
        setError('Failed to fetch KPIs.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, [employeeId, selectedYear]);

  // Update filtered goals when year selection changes
  useEffect(() => {
    if (goals.length > 0) {
      const yearFilteredGoals = filterGoalsByYear(goals, selectedYear);
      setFilteredGoals(yearFilteredGoals);
    }
  }, [selectedYear, goals]);

  // Handle Review button click
  const handleReviewClick = (goal) => {
    setSelectedGoal(goal);
    setHodScore('');
    setModalError('');
    setModalOpen(true);
  };

  const handleAgreedReviewClick = (goal) => {
    setSelectedGoal(goal);
    setAgreedScore('');
    setModalError('');
    setAgreedModalOpen(true);
  };

  // Handle View button click
  const handleViewClick = (goal) => {
    setViewGoal(goal);
    setViewModalOpen(true);
  };

  // Generate report data
  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });

    const totalScore = filteredGoals.reduce((sum, g) => {
      const gScore = g.agreed_score !== undefined && g.agreed_score !== null
        ? Number(g.agreed_score)
        : (Number(g.coo_score) || 0);
      return sum + gScore;
    }, 0);
    const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
    const percentage = totalMetric > 0 ? ((totalScore / totalMetric) * 100).toFixed(2) : '0.00';

    return {
      employeeName,
      year: selectedYear,
      kras: Object.entries(kraMap),
      totalScore,
      totalMetric,
      percentage,
      goals: filteredGoals
    };
  };

  // Generate Professional PDF Report
  const generatePDF = async () => {
    try {
      const reportData = generateReportData();

      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');

      // Colors
      const primaryColor = [59, 130, 246]; // Blue
      const darkGray = [55, 65, 81]; // Dark gray
      const lightGray = [156, 163, 175]; // Light gray
      const redColor = [220, 38, 38]; // Red for flagged

      // Helper function to wrap text
      const wrapText = (text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';

        for (let word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = doc.getTextWidth(testLine);

          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines;
      };

      // Helper function to draw table
      const drawTable = (headers, rows, startY, maxWidth = 180) => {
        const colWidths = [60, 40, 20, 20, 20, 20]; // Adjust based on content
        const rowHeight = 8;
        const headerHeight = 10;

        // Draw header
        doc.setFillColor(...primaryColor);
        doc.rect(15, startY, maxWidth, headerHeight, 'F');

        let x = 15;
        headers.forEach((header, i) => {
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(header, x + 2, startY + 6);
          x += colWidths[i];
        });

        // Draw rows
        let currentY = startY + headerHeight;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);

        rows.forEach((row, rowIndex) => {
          // Alternate row colors
          if (rowIndex % 2 === 0) {
            doc.setFillColor(249, 250, 251);
            doc.rect(15, currentY, maxWidth, rowHeight, 'F');
          }

          // Draw row content
          x = 15;
          let maxLines = 1;
          row.forEach((cell, colIndex) => {
            const cellText = String(cell || '');
            const wrappedText = wrapText(cellText, colWidths[colIndex] - 4);
            maxLines = Math.max(maxLines, wrappedText.length);

            wrappedText.forEach((line, lineIndex) => {
              doc.text(line, x + 2, currentY + 5 + (lineIndex * 3.5));
            });

            x += colWidths[colIndex];
          });

          currentY += Math.max(rowHeight, maxLines * 3.5 + 2);
        });

        return currentY;
      };

      let yPosition = 20;

      // Header
      doc.setTextColor(...primaryColor);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Report', 105, yPosition, { align: 'center' });

      yPosition += 15;
      doc.setTextColor(...darkGray);
      doc.setFontSize(16);
      doc.setFont(undefined, 'normal');
      doc.text(`for ${employeeName} - Year: ${selectedYear}`, 105, yPosition, { align: 'center' });


      yPosition += 8;


      // Performance Summary Box
      doc.setFillColor(248, 250, 252);
      doc.rect(15, yPosition, 180, 25, 'F');
      doc.setDrawColor(...primaryColor);
      doc.rect(15, yPosition, 180, 35);

      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Summary', 20, yPosition);

      yPosition += 8;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(`Total Final Score: ${reportData.totalScore} / ${reportData.totalMetric}`, 20, yPosition);

      yPosition += 5;
      doc.text(`Performance Percentage: ${reportData.percentage}%`, 20, yPosition);

      yPosition += 5;
      doc.text(`Number of Goals: ${reportData.goals.length}`, 20, yPosition);

      yPosition += 20;

      // KRA Sections
      reportData.kras.forEach(([kraName, kraGoals]) => {
        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        // KRA Header
        doc.setFillColor(...primaryColor);
        doc.rect(15, yPosition, 180, 12, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kraName}`, 20, yPosition + 8);

        yPosition += 15;

        // Prepare table data
        const scoreHeader = 'Final Score';
        const headers = ['Activity/KPI', 'Standard', 'Metric', scoreHeader];
        const rows = kraGoals.map(goal => {
          const finalScore = goal.agreed_score !== undefined && goal.agreed_score !== null
            ? goal.agreed_score
            : goal.coo_score;

          return [
            goal.activity || 'N/A',
            goal.performance_standard || 'N/A',
            goal.metric || 'N/A',
            finalScore !== undefined ? finalScore : 'Not Reviewed',
          ];
        });

        // Add KRA total row
        const kraTotal = kraGoals.reduce((s, g) => {
          const fScore = g.agreed_score !== undefined && g.agreed_score !== null
            ? Number(g.agreed_score)
            : (Number(g.coo_score) || 0);
          return s + fScore;
        }, 0);
        const kraMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0);
        rows.push(['KRA Total', '', '', `${kraTotal}/${kraMetric}`]);

        // Draw table
        yPosition = drawTable(headers, rows, yPosition);
        yPosition += 15;
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(...lightGray);
        doc.setFontSize(8);
        doc.text('Generated by Performance Management System', 15, 290);
        doc.text(`Page ${i} of ${pageCount}`, 175, 290, { align: 'right' });
      }

      // Save the PDF
      const fileName = `Performance Report for ${employeeName} for ${selectedYear}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  // Handle modal submit
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = selectedGoal.metric;
    const parsedScore = Number(hodScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(`COO score cannot be greater than the goal metric (${maxScore}).`);
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { coo_score: parsedScore },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      // Update the goal in the list
      setGoals(goals => goals.map(g => g.id === selectedGoal.id ? { ...g, coo_score: parsedScore } : g));
      setFilteredGoals(filteredGoals => filteredGoals.map(g => g.id === selectedGoal.id ? { ...g, coo_score: parsedScore } : g));
      setModalOpen(false);
      setSelectedGoal(null);
      setHodScore('');
    } catch (err) {
      setModalError('Failed to submit COO score.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAgreedScoreSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = selectedGoal.metric;
    const parsedScore = Number(agreedScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(`Agreed score cannot be greater than the KPI metric (${maxScore}).`);
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { agreed_score: parsedScore },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      // Update the goal in the list
      const updateGoalsList = (list) => list.map(g => g.id === selectedGoal.id ? { ...g, agreed_score: parsedScore } : g);
      setGoals(prev => updateGoalsList(prev));
      setFilteredGoals(prev => updateGoalsList(prev));
      setAgreedModalOpen(false);
      setSelectedGoal(null);
      setAgreedScore('');
    } catch (err) {
      setModalError('Failed to submit agreed score.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="page-header">
          <div className="header-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 100 }}>
              <h1 style={{ margin: 0 }}>Employee KPIs Review</h1>
              {employeeName && (
                <span style={{ fontSize: 22, fontWeight: 600, color: '#fffb', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {employeeName}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 500, color: '#fff' }}>Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    background: '#fff',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Download PDF Report Button */}
              {filteredGoals.length > 0 && (
                <button
                  onClick={generatePDF}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                    transition: 'all 0.3s ease',
                    width: 'auto',
                    minWidth: 'auto',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = '#b91c1c';

                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.padding = '10px 12px';
                    e.currentTarget.style.minWidth = 'auto';
                  }}
                  title="Download PDF Report"
                >
                  <FaFilePdf style={{ fontSize: 16, marginRight: 4 }} />
                  <FaDownload style={{ fontSize: 14 }} />
                </button>
              )}

              <button
                onClick={() => navigate(-1)}
                style={{
                  background: '#4F46E5',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 18px',
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'background 0.2s',
                }}
              >
                Back
              </button>
            </div>
          </div>
        </div>
        <div className="hr-content-wrapper">
          {loading ? (
            <div className="loading-state"><FaSpinner className="spinner" /> Loading KPIs...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : (
            <>
              <div className="goals-table-wrapper">
                <table className="goals-table">
                  <thead>
                    <tr>
                      <th>KRA</th>
                      <th>Activity</th>
                      <th>Performance Standard</th>
                      <th>Metric</th>
                      <th>COO Score</th>
                      <th>Agreed Score</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGoals.length === 0 ? (
                      <tr><td colSpan={6} className="empty-row">No completed KPIs found for {selectedYear}.</td></tr>
                    ) : (
                      (() => {
                        const kraMap = {};
                        filteredGoals.forEach(g => {
                          const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
                          if (!kraMap[kraName]) kraMap[kraName] = [];
                          kraMap[kraName].push(g);
                        });
                        const grouped = Object.entries(kraMap);
                        return grouped.map(([kra, kraGoals], gidx) => (
                          <React.Fragment key={kra || gidx}>
                            <tr style={{ background: '#fffb' }}>
                              <th colSpan={6} style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#334155', width: '100%' }}>
                                KRA: {kra}
                              </th>
                            </tr>
                            {kraGoals.map((goal, idx) => {
                              const currentScore = goal.coo_score;
                              const isUnreviewed = (currentScore === 0 || currentScore === undefined);
                              return (
                                <tr
                                  key={goal.id || idx}
                                  className={isUnreviewed ? 'unreviewed-row' : ''}
                                >
                                  <td>{kra}</td>
                                  <td>{goal.activity || '[No Activity]'}</td>
                                  <td>{goal.performance_standard !== undefined ? goal.performance_standard : '-'}</td>
                                  <td>{goal.metric !== undefined ? goal.metric : '-'}</td>
                                  <td>{goal.coo_score !== undefined ? goal.coo_score : '-'}</td>
                                  <td>{goal.agreed_score !== undefined ? goal.agreed_score : '-'}</td>
                                  <td>
                                    <div className="goal-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                                      {goal.flagged ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                          <MdFlag style={{ color: '#ef4444', fontSize: 20 }} />
                                          <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 13 }}>Flagged</span>
                                        </span>
                                      ) : null}

                                      {/* Initial Review Button */}
                                      {currentScore === 0 || currentScore === undefined ? (
                                        <button
                                          className="review-button"
                                          onClick={() => handleReviewClick(goal)}
                                        >
                                          Review
                                        </button>
                                      ) : (
                                        <span style={{ color: '#22c55e', fontWeight: 600, backgroundColor: '#e2f5ea', padding: '4px 8px', borderRadius: 4 }}>Reviewed</span>
                                      )}

                                      {/* Agreed Score Review Button */}
                                      {currentScore !== 0 && currentScore !== undefined && (goal.agreed_score === 0 || goal.agreed_score === undefined) ? (
                                        <button
                                          className="review-button agreed-review-btn"
                                          onClick={() => handleAgreedReviewClick(goal)}
                                          style={{ background: '#4F46E5', color: '#fff' }}
                                        >
                                          Agree Score
                                        </button>
                                      ) : goal.agreed_score !== 0 && goal.agreed_score !== undefined ? (
                                        <span style={{ color: '#4338ca', fontWeight: 700, backgroundColor: '#e0e7ff', padding: '4px 8px', borderRadius: 4 }}>Agreed</span>
                                      ) : null}

                                      <button className='view-button action-button' onClick={() => handleViewClick(goal)}>
                                        <FaEye />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {/* KRA subtotal row */}
                            <tr>
                              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, background: '#f8fafc' }}>KRA Final</td>
                              <td colSpan={2} style={{ fontWeight: 800, color: '#0f172a', background: '#f8fafc' }}>
                                {kraGoals.reduce((s, g) => {
                                  const gScore = g.agreed_score !== undefined && g.agreed_score !== null
                                    ? Number(g.agreed_score)
                                    : (Number(g.coo_score) || 0);
                                  return s + gScore;
                                }, 0)} / {kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)}
                              </td>
                              <td style={{ background: '#f8fafc' }}></td>
                            </tr>
                          </React.Fragment>
                        ))
                      })()
                    )}
                  </tbody>
                </table>
              </div>
              {filteredGoals.length > 0 ? (() => {
                const totalScore = filteredGoals.reduce((sum, g) => {
                  const gScore = g.agreed_score !== undefined && g.agreed_score !== null
                    ? Number(g.agreed_score)
                    : (Number(g.coo_score) || 0);
                  return sum + gScore;
                }, 0);
                const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
                const pct = totalMetric > 0 ? ((totalScore / totalMetric) * 100).toFixed(2) : '0.00';
                return (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: '10px 16px',
                      boxShadow: '0 2px 8px rgba(30,41,59,0.06)'
                    }}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>Final Total</div>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>{totalScore} / {totalMetric}</div>
                      <div style={{
                        marginLeft: 8,
                        background: '#e0f2fe',
                        color: '#0369a1',
                        fontWeight: 800,
                        padding: '4px 10px',
                        borderRadius: 9999,
                        border: '1px solid #bae6fd'
                      }}>{pct}%</div>
                    </div>
                  </div>
                );
              })() : null}

            </>
          )}
        </div>
      </div>
      {/* Modal for HOD score input */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 700, fontSize: 22, color: '#0f172a', letterSpacing: 0.2 }}>Review Goal</h2>
        {selectedGoal && (
          <form onSubmit={handleModalSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontSize: 15, color: '#334155', marginBottom: 2 }}>Activity</div>
              <div style={{ fontWeight: 500, color: '#0f172a' }}>{selectedGoal.activity}</div>
            </div>
            <div>
              <div style={{ fontSize: 15, color: '#334155', marginBottom: 2 }}>KPI Metric ({selectedGoal.metric})</div>
              <div style={{ fontWeight: 500, color: '#0f172a' }}></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label htmlFor="hod-score-input" style={{ fontSize: 15, color: '#334155' }}>COO Score</label>
              <input
                id="hod-score-input"
                type="number"
                min="0"
                max={selectedGoal.metric}
                value={hodScore}
                onChange={e => setHodScore(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 16,
                  width: 120,
                  outline: 'none',
                  transition: 'border 0.2s',
                  boxSizing: 'border-box',
                }}
                required
                disabled={submitting}
              />
            </div>
            {modalError && <div style={{ color: '#ef4444', background: '#fef2f2', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}>{modalError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? '#a7f3d0' : '#22c55e',
                  color: '#fff',
                  padding: '8px 22px',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 1px 2px rgba(16,185,129,0.08)',
                  transition: 'background 0.2s',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={submitting}
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  padding: '8px 18px',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 500,
                  fontSize: 16,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  borderBottom: '2px solid #e2e8f0',
                  transition: 'background 0.2s',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
      {/* Modal for Agreed score input */}
      <Modal isOpen={agreedModalOpen} onClose={() => setAgreedModalOpen(false)}>
        <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 700, fontSize: 22, color: '#0f172a', letterSpacing: 0.2 }}>Agree Final Score</h2>
        {selectedGoal && (
          <form onSubmit={handleAgreedScoreSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontSize: 15, color: '#334155', marginBottom: 2 }}>Activity</div>
              <div style={{ fontWeight: 500, color: '#0f172a' }}>{selectedGoal.activity}</div>
            </div>
            <div style={{ display: 'flex', gap: 20 }}>
              <div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 2 }}>Metric</div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedGoal.metric}</div>
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 2 }}>Self Score</div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedGoal.score}</div>
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 2 }}>COO Score</div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedGoal.coo_score}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label htmlFor="agreed-score-input" style={{ fontSize: 15, color: '#334155' }}>Agreed Final Score</label>
              <input
                id="agreed-score-input"
                type="number"
                min="0"
                max={selectedGoal.metric}
                value={agreedScore}
                onChange={e => setAgreedScore(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 16,
                  width: 120,
                  outline: 'none',
                  transition: 'border 0.2s',
                  boxSizing: 'border-box',
                }}
                required
                disabled={submitting}
              />
            </div>
            {modalError && <div style={{ color: '#ef4444', background: '#fef2f2', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}>{modalError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? '#a5b4fc' : '#4F46E5',
                  color: '#fff',
                  padding: '8px 22px',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 1px 2px rgba(79, 70, 229, 0.1)',
                  transition: 'background 0.2s',
                }}
              >
                {submitting ? 'Submitting...' : 'Agree Score'}
              </button>
              <button
                type="button"
                onClick={() => setAgreedModalOpen(false)}
                disabled={submitting}
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  padding: '8px 18px',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 500,
                  fontSize: 16,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  borderBottom: '2px solid #e2e8f0',
                  transition: 'background 0.2s',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
      {/* Modal for viewing goal details */}
      {viewModalOpen && viewGoal && (
        <div className="modal-overlay">
          <div className="modal-content-employee-goal">
            <div className="modal-header">
              <h2>Goal Details</h2>
              <button onClick={() => setViewModalOpen(false)} className="modal-close-btn">&times;</button>
            </div>
            <div className="modal-body">
              <div className="goal-details-grid">
                <div className="goal-detail-row">
                  <label>Key Result Area:</label>
                  <span>{viewGoal.key_result_area?.name || viewGoal.kra_name || 'Uncategorized'}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Activity:</label>
                  <span>{viewGoal.activity}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Performance Standard:</label>
                  <span>{viewGoal.performance_standard || '-'}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Metric:</label>
                  <span>{viewGoal.metric || viewGoal.score || '-'}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Self Score:</label>
                  <span>{viewGoal.score || '-'}</span>
                </div>
                <div className="goal-detail-row">
                  <label>COO Score:</label>
                  <span>{viewGoal.coo_score !== undefined ? viewGoal.coo_score : '-'}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Agreed Score:</label>
                  <span>{viewGoal.agreed_score !== undefined && viewGoal.agreed_score !== null ? viewGoal.agreed_score : '-'}</span>
                </div>

                {viewGoal.flagged && viewGoal.flagging_reason && (
                  <div className="goal-detail-row full-width">
                    <label>Flag Reason:</label>
                    <span className="rejection-reason">{viewGoal.flagging_reason}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setViewModalOpen(false)} className="modal-close-button">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeGoalsReview;
